/**
 * ============================================================================
 * Admin Emissions Page - Client Emissions Management
 * ============================================================================
 * 
 * Manage emission entries for all clients with detailed view and Excel export.
 * 
 * Features:
 * - List all emission entries with company and period filters
 * - View detailed emission data with activity indicators and factors
 * - Export emission data to Excel with multiple sheets
 * - Filter by company, period, category
 * - Real-time statistics
 * 
 * ============================================================================
 */

import { useState } from "react";
import { useTranslation } from "react-i18next";
import DashboardLayout from "@/components/DashboardLayout";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  BarChart3,
  Download,
  Filter,
  Search,
  RefreshCw,
  TrendingUp,
  Building2,
  Calendar,
  Eye,
  FileSpreadsheet,
  Loader2,
} from "lucide-react";
import { toast } from "sonner";
import { trpc } from "@/lib/trpc";
import { exportEmissionsToExcel, EmissionExportData, EmissionDataRow } from "@/lib/excelExport";

interface ClientEmissionEntry {
  id: string;
  companyId: string;
  companyName: string;
  periodId: string;
  periodName: string;
  periodYear: number;
  startDate?: string;
  endDate?: string;
  category: string;
  subcategory?: string;
  processName: string;
  scope: string;
  activityValue: number;
  unit: string;
  feValue: number;
  tco2e: number;
  notes?: string;
  createdAt: string;
}

interface CompanyPeriodGroup {
  companyId: string;
  companyName: string;
  periodId: string;
  periodName: string;
  periodYear: number;
  startDate?: string;
  endDate?: string;
  emissions: ClientEmissionEntry[];
  totalEmissions: number;
  scope1: number;
  scope2: number;
  scope3: number;
}

export default function AdminEmissions() {
  const { t } = useTranslation();
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedCompany, setSelectedCompany] = useState<string>("all");
  const [selectedPeriod, setSelectedPeriod] = useState<string>("all");
  const [isExporting, setIsExporting] = useState(false);

  // State for detailed view
  const [selectedGroup, setSelectedGroup] = useState<CompanyPeriodGroup | null>(null);
  const [isDetailDialogOpen, setIsDetailDialogOpen] = useState(false);

  // Fetch emissions using tRPC
  const { data: emissionsData = [], isLoading, refetch } = trpc.admin.getAllEmissions.useQuery(
    {
      companyId: selectedCompany !== "all" ? selectedCompany : undefined,
      periodId: selectedPeriod !== "all" ? selectedPeriod : undefined,
    },
    {
      onError: (error) => {
        console.error("Error loading emissions:", error);
        toast.error(t("admin.emissions.loadError") || "Erreur lors du chargement des données");
      },
      onSuccess: () => {
        if (selectedCompany !== "all" || selectedPeriod !== "all") {
          toast.success(t("admin.emissions.dataLoaded") || "Données chargées avec succès");
        }
      },
    }
  );

  // Transform tRPC data to ClientEmissionEntry format
  const emissions: ClientEmissionEntry[] = emissionsData.map((e: any) => ({
    id: e.id,
    companyId: e.period?.company_id || e.companyId || "",
    companyName: e.period?.company?.name || "Unknown",
    periodId: e.period_id || e.periodId || "",
    periodName: e.period?.period_label || "Unknown",
    periodYear: new Date(e.created_at).getFullYear(),
    startDate: e.period?.start_date,
    endDate: e.period?.end_date,
    category: e.fe?.category || "Other",
    subcategory: e.fe?.subcategory,
    processName: e.fe?.process_name || "Unknown",
    scope: e.fe?.scope || "Scope 3",
    activityValue: Number(e.activity_value) || 0,
    unit: e.fe?.unit || e.unit || "",
    feValue: Number(e.fe?.factor_value) || 0,
    tco2e: Number(e.tco2e) || 0,
    notes: e.notes,
    createdAt: e.created_at,
  }));

  // Calculate statistics
  const total = emissions.reduce((sum, e) => sum + e.tco2e, 0);
  const companiesSet = new Set(emissions.map((e) => e.companyId));
  const stats = {
    totalEmissions: total,
    totalCompanies: companiesSet.size,
    totalEntries: emissions.length,
    averagePerCompany: companiesSet.size > 0 ? total / companiesSet.size : 0,
  };

  const filteredEmissions = emissions.filter((emission) => {
    const matchesSearch =
      emission.companyName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emission.category.toLowerCase().includes(searchTerm.toLowerCase()) ||
      emission.processName.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesCompany =
      selectedCompany === "all" || emission.companyId === selectedCompany;
    const matchesPeriod =
      selectedPeriod === "all" || emission.periodId === selectedPeriod;

    return matchesSearch && matchesCompany && matchesPeriod;
  });

  // Group emissions by company and period
  const groupedEmissions = filteredEmissions.reduce((acc, emission) => {
    const key = `${emission.companyId}-${emission.periodId}`;
    if (!acc[key]) {
      acc[key] = {
        companyId: emission.companyId,
        companyName: emission.companyName,
        periodId: emission.periodId,
        periodName: emission.periodName,
        periodYear: emission.periodYear,
        startDate: emission.startDate,
        endDate: emission.endDate,
        emissions: [],
        totalEmissions: 0,
        scope1: 0,
        scope2: 0,
        scope3: 0,
      };
    }
    acc[key].emissions.push(emission);
    acc[key].totalEmissions += emission.tco2e;

    if (emission.scope === "Scope 1") {
      acc[key].scope1 += emission.tco2e;
    } else if (emission.scope === "Scope 2") {
      acc[key].scope2 += emission.tco2e;
    } else if (emission.scope === "Scope 3") {
      acc[key].scope3 += emission.tco2e;
    }

    return acc;
  }, {} as Record<string, CompanyPeriodGroup>);

  const groupedEmissionsArray = Object.values(groupedEmissions);

  // Get unique companies and periods for filters
  const uniqueCompanies = Array.from(new Set(emissions.map((e) => e.companyId))).map((id) => {
    const company = emissions.find((e) => e.companyId === id);
    return { id, name: company?.companyName || "" };
  });

  const uniquePeriods = Array.from(new Set(emissions.map((e) => e.periodId))).map((id) => {
    const period = emissions.find((e) => e.periodId === id);
    return { id, name: period?.periodName || "" };
  });

  const handleViewDetails = (group: CompanyPeriodGroup) => {
    setSelectedGroup(group);
    setIsDetailDialogOpen(true);
  };

  const handleExportExcel = async (group: CompanyPeriodGroup) => {
    setIsExporting(true);
    try {
      // Convert to export format
      const exportData: EmissionDataRow[] = group.emissions.map((e) => ({
        id: parseInt(e.id) || 0,
        category: e.category,
        subcategory: e.subcategory,
        processName: e.processName,
        scope: e.scope,
        activityValue: e.activityValue,
        unit: e.unit,
        feValue: e.feValue,
        tco2e: e.tco2e,
        createdAt: e.createdAt,
        notes: e.notes,
      }));

      const emissionExportData: EmissionExportData = {
        companyName: group.companyName,
        periodLabel: group.periodName,
        year: group.periodYear,
        startDate: group.startDate,
        endDate: group.endDate,
        emissions: exportData,
        totalEmissions: group.totalEmissions,
        scope1: group.scope1,
        scope2: group.scope2,
        scope3: group.scope3,
      };

      await exportEmissionsToExcel(emissionExportData);
      toast.success(t("admin.emissions.exportSuccess") || "Export Excel réussi");
    } catch (error) {
      console.error("Error exporting to Excel:", error);
      toast.error(t("admin.emissions.exportError") || "Erreur lors de l'export");
    } finally {
      setIsExporting(false);
    }
  };

  return (
    <DashboardLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">
              {t("admin.emissions.title") || "Émissions Clients"}
            </h1>
            <p className="text-muted-foreground">
              {t("admin.emissions.description") || "Gérez et analysez les données d'émissions carbone de vos clients"}
            </p>
          </div>
          <div className="flex gap-2">
            <Button onClick={() => refetch()} variant="outline" disabled={isLoading}>
              <RefreshCw className={`h-4 w-4 mr-2 ${isLoading ? "animate-spin" : ""}`} />
              {t("common.refresh") || "Actualiser"}
            </Button>
          </div>
        </div>

        {/* Statistics Cards */}
        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("admin.emissions.totalEmissions") || "Émissions Totales"}
              </CardTitle>
              <TrendingUp className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalEmissions.toFixed(2)} tCO₂e</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("admin.emissions.totalCompanies") || "Entreprises"}
              </CardTitle>
              <Building2 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalCompanies}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("admin.emissions.totalEntries") || "Entrées"}
              </CardTitle>
              <BarChart3 className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.totalEntries}</div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
              <CardTitle className="text-sm font-medium">
                {t("admin.emissions.averagePerCompany") || "Moyenne/Entreprise"}
              </CardTitle>
              <Calendar className="h-4 w-4 text-muted-foreground" />
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{stats.averagePerCompany.toFixed(2)} tCO₂e</div>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Filter className="h-5 w-5" />
              {t("admin.emissions.filters") || "Filtres"}
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="search">{t("common.search") || "Rechercher"}</Label>
                <div className="relative">
                  <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
                  <Input
                    id="search"
                    placeholder={t("admin.emissions.searchPlaceholder") || "Rechercher..."}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="pl-8"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="company">{t("admin.emissions.filterByCompany") || "Entreprise"}</Label>
                <Select value={selectedCompany} onValueChange={setSelectedCompany}>
                  <SelectTrigger id="company">
                    <SelectValue placeholder={t("common.selectCompany") || "Sélectionner"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all") || "Toutes"}</SelectItem>
                    {uniqueCompanies.map((company) => (
                      <SelectItem key={company.id} value={company.id}>
                        {company.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div className="space-y-2">
                <Label htmlFor="period">{t("common.period") || "Période"}</Label>
                <Select value={selectedPeriod} onValueChange={setSelectedPeriod}>
                  <SelectTrigger id="period">
                    <SelectValue placeholder={t("common.selectPeriod") || "Sélectionner"} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">{t("common.all") || "Toutes"}</SelectItem>
                    {uniquePeriods.map((period) => (
                      <SelectItem key={period.id} value={period.id}>
                        {period.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Grouped Emissions Table */}
        <Card>
          <CardHeader>
            <CardTitle>{t("admin.emissions.emissionsList") || "Résumé par Entreprise et Période"}</CardTitle>
            <CardDescription>
              {t("admin.emissions.listDescription") || "Cliquez sur une ligne pour voir les détails et exporter en Excel"}
              {" "}({groupedEmissionsArray.length} {t("common.results") || "résultats"})
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="rounded-md border">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>{t("common.company") || "Entreprise"}</TableHead>
                    <TableHead>{t("common.period") || "Période"}</TableHead>
                    <TableHead className="text-right">{t("common.entries") || "Entrées"}</TableHead>
                    <TableHead className="text-right">{t("dashboard.totalEmissions") || "Total"}</TableHead>
                    <TableHead className="text-right">{t("dashboard.scope1") || "Scope 1"}</TableHead>
                    <TableHead className="text-right">{t("dashboard.scope2") || "Scope 2"}</TableHead>
                    <TableHead className="text-right">{t("dashboard.scope3") || "Scope 3"}</TableHead>
                    <TableHead className="text-center">{t("common.actions") || "Actions"}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {groupedEmissionsArray.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={8} className="text-center text-muted-foreground">
                        {isLoading
                          ? t("common.loading") || "Chargement..."
                          : t("admin.emissions.noData") || "Aucune donnée disponible"}
                      </TableCell>
                    </TableRow>
                  ) : (
                    groupedEmissionsArray.map((group) => (
                      <TableRow key={`${group.companyId}-${group.periodId}`}>
                        <TableCell className="font-medium">{group.companyName}</TableCell>
                        <TableCell>{group.periodName}</TableCell>
                        <TableCell className="text-right">{group.emissions.length}</TableCell>
                        <TableCell className="text-right font-semibold">
                          {group.totalEmissions.toFixed(2)}
                        </TableCell>
                        <TableCell className="text-right">{group.scope1.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{group.scope2.toFixed(2)}</TableCell>
                        <TableCell className="text-right">{group.scope3.toFixed(2)}</TableCell>
                        <TableCell className="text-center">
                          <div className="flex gap-2 justify-center">
                            <Dialog open={isDetailDialogOpen && selectedGroup?.companyId === group.companyId} onOpenChange={setIsDetailDialogOpen}>
                              <DialogTrigger asChild>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleViewDetails(group)}
                                  title={t("common.viewDetails") || "Voir les détails"}
                                >
                                  <Eye className="h-4 w-4" />
                                </Button>
                              </DialogTrigger>
                              <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
                                <DialogHeader>
                                  <DialogTitle>
                                    {selectedGroup?.companyName} - {selectedGroup?.periodName}
                                  </DialogTitle>
                                  <DialogDescription>
                                    {t("admin.emissions.detailedView") || "Vue détaillée des émissions"}
                                  </DialogDescription>
                                </DialogHeader>
                                {selectedGroup && (
                                  <div className="space-y-4">
                                    {/* Summary */}
                                    <div className="grid gap-4 md:grid-cols-4">
                                      <div className="bg-blue-50 p-4 rounded-lg">
                                        <p className="text-sm text-muted-foreground">Total</p>
                                        <p className="text-2xl font-bold">{selectedGroup.totalEmissions.toFixed(2)}</p>
                                      </div>
                                      <div className="bg-red-50 p-4 rounded-lg">
                                        <p className="text-sm text-muted-foreground">Scope 1</p>
                                        <p className="text-2xl font-bold">{selectedGroup.scope1.toFixed(2)}</p>
                                      </div>
                                      <div className="bg-green-50 p-4 rounded-lg">
                                        <p className="text-sm text-muted-foreground">Scope 2</p>
                                        <p className="text-2xl font-bold">{selectedGroup.scope2.toFixed(2)}</p>
                                      </div>
                                      <div className="bg-purple-50 p-4 rounded-lg">
                                        <p className="text-sm text-muted-foreground">Scope 3</p>
                                        <p className="text-2xl font-bold">{selectedGroup.scope3.toFixed(2)}</p>
                                      </div>
                                    </div>

                                    {/* Detailed Table */}
                                    <div className="rounded-md border">
                                      <Table>
                                        <TableHeader>
                                          <TableRow>
                                            <TableHead>{t("common.category") || "Catégorie"}</TableHead>
                                            <TableHead>{t("common.process") || "Processus"}</TableHead>
                                            <TableHead>{t("common.scope") || "Scope"}</TableHead>
                                            <TableHead className="text-right">{t("common.activity") || "Activité"}</TableHead>
                                            <TableHead>{t("common.unit") || "Unité"}</TableHead>
                                            <TableHead className="text-right">{t("common.factor") || "FE"}</TableHead>
                                            <TableHead className="text-right">tCO₂e</TableHead>
                                          </TableRow>
                                        </TableHeader>
                                        <TableBody>
                                          {selectedGroup.emissions.map((emission) => (
                                            <TableRow key={emission.id}>
                                              <TableCell>{emission.category}</TableCell>
                                              <TableCell className="text-sm">{emission.processName}</TableCell>
                                              <TableCell>
                                                <Badge variant="outline">{emission.scope}</Badge>
                                              </TableCell>
                                              <TableCell className="text-right">
                                                {emission.activityValue.toFixed(2)}
                                              </TableCell>
                                              <TableCell>{emission.unit}</TableCell>
                                              <TableCell className="text-right">
                                                {emission.feValue.toFixed(4)}
                                              </TableCell>
                                              <TableCell className="text-right font-semibold">
                                                {emission.tco2e.toFixed(2)}
                                              </TableCell>
                                            </TableRow>
                                          ))}
                                        </TableBody>
                                      </Table>
                                    </div>

                                    {/* Export Button */}
                                    <div className="flex justify-end gap-2 pt-4">
                                      <Button
                                        onClick={() => handleExportExcel(selectedGroup)}
                                        disabled={isExporting}
                                        className="gap-2"
                                      >
                                        {isExporting ? (
                                          <>
                                            <Loader2 className="h-4 w-4 animate-spin" />
                                            {t("common.exporting") || "Export en cours..."}
                                          </>
                                        ) : (
                                          <>
                                            <FileSpreadsheet className="h-4 w-4" />
                                            {t("common.exportExcel") || "Télécharger Excel"}
                                          </>
                                        )}
                                      </Button>
                                    </div>
                                  </div>
                                )}
                              </DialogContent>
                            </Dialog>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleExportExcel(group)}
                              disabled={isExporting}
                              title={t("common.exportExcel") || "Exporter en Excel"}
                            >
                              {isExporting ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <Download className="h-4 w-4" />
                              )}
                            </Button>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
}
